import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm1_u2_x1_abluefter';

const M1_U2_X1_Abluefter: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  const canEditTexts = canEditTextsM1;
  const canUploadImages = canUploadImagesM1;

  // Override laden und Refresh bereitstellen
  const { override, refresh } = useUnitOverride(PAGE_ID);

  // Basistexte
  const base = {
    intro:
      'Abluftanlagen sind ein integraler Bestandteil moderner Gebäude. Sie dienen dazu, verbrauchte, verunreinigte oder gefährliche Luft aus Innenräumen abzuführen, um Gesundheit, Sicherheit und Komfort zu gewährleisten.',
    notwendigkeit:
      'Abluftanlagen dienen zum Abtransport verbrauchter Luft und gewährleisten Gesundheit, Sicherheit und Komfort.\n\n• Gesundheitsschutz: Abtransport von Schadstoffen, Dämpfen, Viren und Partikeln; Reduktion von Feuchtigkeit und Schimmel.\n• Sicherheit: Abtransport von brennbaren oder explosiven Gasen.\n• Komfort: Verbesserung der Luftqualität und Abtransport von Wärme und Gerüchen.',
    funktionsarten:
      'Abhängig von den Anforderungen können Abluftanlagen unterschiedlich konzipiert werden:\n\n• Permanentbetrieb: Kontinuierlicher Betrieb (z.B. Gefahrstofflager).\n• Bedarfsbetrieb: Einschalten bei Bedarf (z.B. über Lichtschalter im WC).\n• Zeitbetrieb: Betrieb nach einem festen Zeitplan.\n• Notbetrieb: Aktivierung bei Störungen oder als Sicherheitsfunktion.\n• Unterdruck/Überdruck: Gezielte Steuerung von Druckverhältnissen zur Kontaminationskontrolle.',
    glt:
      'Die Ansteuerung über eine GLT ermöglicht eine intelligente und effiziente Betriebsweise:\n\n• Zentrale Steuerung und Überwachung aller Anlagen.\n• Automatisierung der Betriebsabläufe nach Zeit, Bedarf oder Sensordaten.\n• Optimierung der Energieeffizienz durch bedarfsgerechte Regelung.\n• Protokollierung von Betriebsstunden und Fehlern.\n• Integration mit anderen Systemen (z.B. Brandschutz, Zutrittskontrolle).',
    anwendungen: '',
    chemikalien:
      'Funktionsart: Permanentbetrieb und Notbetrieb, Erzeugung von Unterdruck.\nGLT-Steuerung: Permanente Überwachung, Alarmierung bei Grenzwertüberschreitung (z.B. CO, O2), Notbetrieb, Verriegelung von Türen.\nMeldungen: Systemfehler, Filterstatus, Klappenstellung, Ventilatorstatus, Volumenstrom.',
    digestorium:
      'Funktionsart: Bedarfsbetrieb.\nGLT-Steuerung: Einschalten der Abluft bei Nutzung des Abzugs, einstellbarer Nachlauf nach Abschalten, Überwachung der Frontschieberstellung.',
    batterieraum:
      'Funktionsart: Zeit- oder Bedarfsbetrieb (gekoppelt an Ladevorgang).\nGLT-Steuerung: Aktivierung während des Ladens zur Abfuhr von Wasserstoff (H2), Überwachung der Gaskonzentration, Alarmierung und Zwangsbelüftung.',
    muellraum:
      'Funktionsart: Zeitbetrieb zur Geruchsminimierung.\nGLT-Steuerung: Aktivierung nach Zeitplan (z.B. mehrmals täglich für kurze Intervalle).',
    maschinenraum:
      'Funktionsart: Bedarfsbetrieb zur Wärmeabfuhr.\nGLT-Steuerung: Aktivierung durch Temperatursensoren bei Überschreitung eines Grenzwertes, Anpassung der Lüfterdrehzahl.',
    sonstige:
      'Funktionsart: Bedarfsbetrieb.\nGLT-Steuerung: Einschalten gekoppelt an das Licht oder über Präsenzmelder mit einstellbarer Nachlaufzeit.',
    wichtig:
      'Filter: In vielen Fällen muss die Abluft gefiltert werden, um die Umwelt zu schützen.\nVolumenstrom: Der Volumenstrom muss den Anforderungen des Raumes und der Nutzung angepasst sein.\nBrandschutz: Abluftanlagen müssen sorgfältig in das Brandschutzkonzept integriert werden (z.B. mit Brandschutzklappen).\nWartung: Regelmäßige Wartung von Ventilatoren und Filtern ist für einen sicheren und effizienten Betrieb unerlässlich.'
  };

  // Blöcke inkl. Override-Fallback
  const blocks = React.useMemo(() => ([
    { id: 'block_intro', heading: 'Abluftanlagen: Notwendigkeit, Funktionsarten und GLT-Steuerung', body: override?.texts?.['block_intro'] ?? base.intro },
    { id: 'block_notwendigkeit', heading: '1. Notwendigkeit von Abluftanlagen', body: override?.texts?.['block_notwendigkeit'] ?? base.notwendigkeit },
    { id: 'block_funktionsarten', heading: '2. Verschiedene Funktionsarten', body: override?.texts?.['block_funktionsarten'] ?? base.funktionsarten },
    { id: 'block_glt', heading: '3. Ansteuerung durch die Gebäudeleittechnik (GLT)', body: override?.texts?.['block_glt'] ?? base.glt },
    { id: 'block_anwendungen', heading: '4. Anwendungsbeispiele', body: override?.texts?.['block_anwendungen'] ?? base.anwendungen },
    { id: 'block_chemikalien', heading: 'Chemikalien-/Gefahrstoffabluft', body: override?.texts?.['block_chemikalien'] ?? base.chemikalien },
    { id: 'block_digestorium', heading: 'Abluft Digestorium (Laborabzug)', body: override?.texts?.['block_digestorium'] ?? base.digestorium },
    { id: 'block_batterieraum', heading: 'Batterieräume', body: override?.texts?.['block_batterieraum'] ?? base.batterieraum },
    { id: 'block_muellraum', heading: 'Müllräume', body: override?.texts?.['block_muellraum'] ?? base.muellraum },
    { id: 'block_maschinenraum', heading: 'Maschinenräume', body: override?.texts?.['block_maschinenraum'] ?? base.maschinenraum },
    { id: 'block_sonstige', heading: 'Sonstige Räume (z.B. WCs, Umkleiden)', body: override?.texts?.['block_sonstige'] ?? base.sonstige },
    { id: 'block_wichtig', heading: 'Weitere wichtige Punkte', body: override?.texts?.['block_wichtig'] ?? base.wichtig }
  ]), [
    override,
    base.anwendungen,
    base.batterieraum,
    base.chemikalien,
    base.digestorium,
    base.funktionsarten,
    base.glt,
    base.intro,
    base.maschinenraum,
    base.muellraum,
    base.notwendigkeit,
    base.sonstige,
    base.wichtig
  ]);

  // TTS Text zusammenstellen aus aktuellen Blöcken
  const fullPlainText = React.useMemo(() => blocks.map(b => `${b.heading}. ${b.body}`).join(' '), [blocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  // Admin: Bild ersetzen
  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  // Admin: Text speichern
  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  // Bildquellen inkl. Override-Fallbacks (unified pattern)
  const imgSrc = (slot: string, fallback: string) => {
    const v = override?.images?.[slot];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const imgSources: Record<string, string> = {
    img_0: imgSrc('img_0', '/assets/drawable/abluefter.jpeg'),
    img_1: imgSrc('img_1', '/assets/drawable/abluft_digi.jpeg'),
    img_2: imgSrc('img_2', '/assets/drawable/abluefer_glt.PNG'),
    img_3: imgSrc('img_3', '/assets/drawable/abluefter_glt_giftschrank.PNG'),
    img_4: imgSrc('img_4', '/assets/drawable/abluft_digi01.png'),
    img_5: imgSrc('img_5', '/assets/drawable/abluefter_batterieraum.jpeg'),
    img_6: imgSrc('img_6', '/assets/drawable/abluft_muellraum.webp')
  };

  return (
    <UnitPageShell title="1.2.1 Ablüfter" actions={ttsButton}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Linke Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_intro"
            heading="Abluftanlagen: Notwendigkeit, Funktionsarten und GLT-Steuerung"
            body={blocks[0].body}
            onSave={(newBody) => handleTextSave('block_intro', newBody)}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_notwendigkeit"
            heading="1. Notwendigkeit von Abluftanlagen"
            body={blocks[1].body}
            onSave={(newBody) => handleTextSave('block_notwendigkeit', newBody)}
          />

          <ZoomableImage
            src={imgSources.img_0}
            alt="Schema einer Abluftanlage"
            label="Bild einfügen"
            onReplace={canUploadImages ? (file) => handleImageReplace('img_0', file) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_funktionsarten"
            heading="2. Verschiedene Funktionsarten"
            body={blocks[2].body}
            onSave={(newBody) => handleTextSave('block_funktionsarten', newBody)}
          />

          <ZoomableImage
            src={imgSources.img_1}
            alt="Schema Digistorium Schrank"
            label="Bild einfügen"
            onReplace={canUploadImages ? (file) => handleImageReplace('img_1', file) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_glt"
            heading="3. Ansteuerung durch die Gebäudeleittechnik (GLT)"
            body={blocks[3].body}
            onSave={(newBody) => handleTextSave('block_glt', newBody)}
          />

          <ZoomableImage
            src={imgSources.img_2}
            alt="GLT Ansteuerung Schema"
            label="Bild einfügen"
            onReplace={canUploadImages ? (file) => handleImageReplace('img_2', file) : undefined}
          />
        </Box>

        {/* Rechte Spalte */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_anwendungen"
            heading="4. Anwendungsbeispiele"
            body={blocks[4].body}
            onSave={(newBody) => handleTextSave('block_anwendungen', newBody)}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_chemikalien"
            heading="Chemikalien-/Gefahrstoffabluft"
            body={blocks[5].body}
            onSave={(newBody) => handleTextSave('block_chemikalien', newBody)}
          />

          <ZoomableImage
            src={imgSources.img_3}
            alt="Schema Giftschrank Abluft"
            label="Bild einfügen"
            onReplace={canUploadImages ? (file) => handleImageReplace('img_3', file) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_digestorium"
            heading="Abluft Digestorium (Laborabzug)"
            body={blocks[6].body}
            onSave={(newBody) => handleTextSave('block_digestorium', newBody)}
          />

          <ZoomableImage
            src={imgSources.img_4}
            alt="Schema Digestorium Abluft"
            label="Bild einfügen"
            onReplace={canUploadImages ? (file) => handleImageReplace('img_4', file) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_batterieraum"
            heading="Batterieräume"
            body={blocks[7].body}
            onSave={(newBody) => handleTextSave('block_batterieraum', newBody)}
          />

          <ZoomableImage
            src={imgSources.img_5}
            alt="Schema Batterieraum Abluft"
            label="Bild einfügen"
            onReplace={canUploadImages ? (file) => handleImageReplace('img_5', file) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_muellraum"
            heading="Müllräume"
            body={blocks[8].body}
            onSave={(newBody) => handleTextSave('block_muellraum', newBody)}
          />

          <ZoomableImage
            src={imgSources.img_6}
            alt="Schema Müllraum Abluft"
            label="Bild einfügen"
            onReplace={canUploadImages ? (file) => handleImageReplace('img_6', file) : undefined}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_maschinenraum"
            heading="Maschinenräume"
            body={blocks[9].body}
            onSave={(newBody) => handleTextSave('block_maschinenraum', newBody)}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_sonstige"
            heading="Sonstige Räume (z.B. WCs, Umkleiden)"
            body={blocks[10].body}
            onSave={(newBody) => handleTextSave('block_sonstige', newBody)}
          />

          <EditableTextBlock
            pageId={PAGE_ID}
            blockId="block_wichtig"
            heading="Weitere wichtige Punkte"
            body={blocks[11].body}
            onSave={(newBody) => handleTextSave('block_wichtig', newBody)}
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/units/m1_u2_x99_quiz')}>
              Zum Quiz
            </Button>
          </Box>
        </Box>
      </Box>
    </UnitPageShell>
  );
};

export default M1_U2_X1_Abluefter;

